// ---------------------------------------------------------------------------
// ItemsSourcePicker — Existing / Inline / File source dropdown + the matching
// mode-specific body. Used by the Query set and Data set cards.
//
// "Existing" is gated by the optional `existingPicker` slot: when the parent
// passes a node (today: the data set), the option is enabled and the node
// renders below; when null (today: query set), the option is disabled with a
// "Catalog not yet wired" note.
// ---------------------------------------------------------------------------

import {
  Button,
  Dropdown,
  Field,
  MessageBar,
  MessageBarBody,
  Option,
  Textarea,
} from '@fluentui/react-components';

import { useStyles } from '../../EvaluationTemplateForm.style.js';
import type { DataSourceMode } from '../../EvaluationTemplateForm.types.js';
import type { InlineItemsAnalysis } from '../../EvaluationTemplateForm.utils.js';
import { analyzeInlineItems } from '../../EvaluationTemplateForm.utils.js';

interface SourceOption {
  value: DataSourceMode;
  title: string;
  description: string;
}

const SOURCE_OPTIONS: SourceOption[] = [
  {
    value: 'existing',
    title: 'Existing',
    description: 'Pick from the Data Lake catalog',
  },
  {
    value: 'inline',
    title: 'Inline',
    description: 'Paste JSON items directly',
  },
  {
    value: 'file',
    title: 'File',
    description: 'Load from a .json or .jsonl file',
  },
];

export interface ItemsSourcePickerProps {
  mode: DataSourceMode;
  onModeChange: (m: DataSourceMode) => void;
  inlineText: string;
  onInlineChange: (s: string) => void;
  inlineAnalysis?: InlineItemsAnalysis;
  fileName: string | null;
  fileItemCount: number | null;
  fileAnalysis?: InlineItemsAnalysis | null;
  fileError: string | null;
  onFile: (f: File) => Promise<void>;
  /** Unique DOM id for the hidden <input type="file">. */
  fieldId: string;
  /** When provided, the "Existing" option is enabled and selecting it
   *  renders this node. When null, "Existing" is disabled with a
   *  "Catalog not yet wired" note. */
  existingPicker?: React.ReactNode | null;
  validationErrors?: string[];
}

export function ItemsSourcePicker({
  mode,
  onModeChange,
  inlineText,
  onInlineChange,
  inlineAnalysis: providedInlineAnalysis,
  fileName,
  fileItemCount,
  fileAnalysis = null,
  fileError,
  onFile,
  fieldId,
  existingPicker = null,
  validationErrors = [],
}: ItemsSourcePickerProps) {
  const styles = useStyles();
  const existingDisabled = existingPicker === null;
  const selected = SOURCE_OPTIONS.find((o) => o.value === mode);
  const inlineAnalysis =
    providedInlineAnalysis ?? analyzeInlineItems(inlineText);
  const fileWarning =
    fileError === null && fileAnalysis?.warning !== undefined
      ? fileAnalysis.warning
      : null;
  return (
    <>
      <Field
        label="Source"
        orientation="horizontal"
        className={styles.inlineField}
      >
        <Dropdown
          aria-label="Source"
          value={selected?.title ?? ''}
          selectedOptions={[mode]}
          onOptionSelect={(_e, data) => {
            if (data.optionValue) {
              onModeChange(data.optionValue as DataSourceMode);
            }
          }}
        >
          {SOURCE_OPTIONS.map((opt) => {
            const disabled = opt.value === 'existing' && existingDisabled;
            return (
              <Option
                key={opt.value}
                value={opt.value}
                text={opt.title}
                disabled={disabled}
              >
                <div className={styles.optionStack}>
                  <span className={styles.segmentTitle}>{opt.title}</span>
                  <span className={styles.segmentDescription}>
                    {opt.description}
                  </span>
                  {disabled && (
                    <span className={styles.segmentNote}>
                      Catalog not yet wired
                    </span>
                  )}
                </div>
              </Option>
            );
          })}
        </Dropdown>
      </Field>

      {mode === 'existing' && existingPicker !== null && existingPicker}
      {mode === 'existing' &&
        validationErrors.map((error) => (
          <span key={error} className={styles.errorText} role="alert">
            {error}
          </span>
        ))}

      {mode === 'inline' && (
        <>
          <Field
            label="Items"
            validationState={validationErrors.length > 0 ? 'error' : 'none'}
            validationMessage={
              validationErrors.length > 0 ? (
                <span>{validationErrors.join(' ')}</span>
              ) : undefined
            }
          >
            <Textarea
              value={inlineText}
              onChange={(_e, data) => onInlineChange(data.value)}
              placeholder={
                '[\n  "first item",\n  "second item"\n]\n\n— or one JSON value per line.'
              }
              rows={6}
            />
          </Field>
          <div className={styles.fieldGroup}>
            <span className={styles.helperText} role="status">
              {inlineText.trim().length > 0 &&
                inlineAnalysis.error === undefined &&
                validationErrors.length === 0 &&
                '✅ '}
              {formatItemCount(inlineAnalysis.count)}
            </span>
            {inlineAnalysis.invalidCount > 0 &&
              inlineAnalysis.warning !== undefined && (
                <span className={styles.helperText}>
                  {inlineAnalysis.warning}
                </span>
              )}
            {inlineAnalysis.error !== undefined && (
              <MessageBar intent="error" role="alert">
                <MessageBarBody>{inlineAnalysis.error}</MessageBarBody>
              </MessageBar>
            )}
          </div>
        </>
      )}

      {mode === 'file' && (
        <div className={styles.fieldGroup}>
          <input
            id={fieldId}
            type="file"
            accept=".json,.jsonl,application/json,application/jsonl"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onFile(f);
            }}
          />
          <div className={styles.fileRow}>
            <Button onClick={() => document.getElementById(fieldId)?.click()}>
              {fileName !== null ? 'Replace file' : 'Choose file'}
            </Button>
            {fileName !== null && fileError === null && (
              <span className={styles.helperText}>
                {fileName} — {String(fileItemCount ?? 0)} items
              </span>
            )}
          </div>
          {fileWarning !== null && (
            <span className={styles.helperText}>{fileWarning}</span>
          )}
          {fileError !== null && (
            <span className={styles.errorText} role="alert">
              {fileError}
            </span>
          )}
          {validationErrors.map((error) => (
            <span key={error} className={styles.errorText} role="alert">
              {error}
            </span>
          ))}
        </div>
      )}
    </>
  );
}

function formatItemCount(count: number): string {
  return `${String(count)} ${count === 1 ? 'item' : 'items'}`;
}
